import classnames from 'classnames';
import NextLink from 'next/link';

type TypographyType = {
  className?: string;
  children?: string | React.ReactNode;
  [x: string]: any;
  textColor?: string;
  /**
   * Render the text as a `next/link` anchor (client-side navigation and
   * prefetching) instead of the default element, keeping the typography
   * styles. Replaces the removed `<Link legacyBehavior>` wrapping pattern.
   */
  href?: string;
  /** Forwarded to `next/link` when `href` is set. */
  prefetch?: boolean;
};

type TypographyElementProps = TypographyType & {
  as: 'h1' | 'h2' | 'h3' | 'h4' | 'p';
  className: string;
};

const TypographyElement = ({
  as: Tag,
  href,
  prefetch,
  children,
  ...rest
}: TypographyElementProps) => {
  if (href) {
    return (
      <NextLink href={href} prefetch={prefetch} {...rest}>
        {children}
      </NextLink>
    );
  }
  return <Tag {...rest}>{children}</Tag>;
};

export const H1 = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="h1"
    className={classnames(
      `font-sans text-4xl leading-[44px] font-bold ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const H2 = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="h2"
    className={classnames(
      `font-sans text-3xl font-bold ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const H3 = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="h3"
    className={classnames(
      `font-sans text-2xl font-normal ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const H4 = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="h4"
    className={classnames(
      `font-sans text-xl font-normal ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const BodyRegular = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="p"
    className={classnames(
      `font-open-sans text-base font-normal ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const BodyRegularBold = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="p"
    className={classnames(
      `font-open-sans text-base font-bold ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const BodySmallRegular = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="p"
    className={classnames(
      `font-open-sans text-sm leading-[18px] font-normal ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const BodySmallRegularBold = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="p"
    className={classnames(
      `font-open-sans text-sm leading-[18px] font-bold ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const BodyExtraSmallRegular = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="p"
    className={classnames(
      `font-open-sans text-xs leading-[18px] font-normal ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const BodyExtraSmallRegularBold = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="p"
    className={classnames(
      `font-open-sans text-xs leading-[18px] font-bold ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const CaptionRegular = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="p"
    className={classnames(
      `font-open-sans text-xxs font-normal ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const CaptionBold = ({
  children,
  className,
  textColor = 'text-neutral-800',
  fontStyles = 'text-xxs',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="p"
    className={classnames(
      `font-open-sans font-bold ${fontStyles} ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const SubtextRegular = ({
  children,
  className,
  textColor = 'text-neutral-800',
  fontStyles = 'text-xxxs font-normal',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="p"
    className={classnames(
      `font-open-sans ${fontStyles}  ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);

export const SubtextBold = ({
  children,
  className,
  textColor = 'text-neutral-800',
  ...rest
}: TypographyType) => (
  <TypographyElement
    as="p"
    className={classnames(
      `font-open-sans text-xxxs font-bold ${textColor}`,
      className
    )}
    {...rest}
  >
    {children}
  </TypographyElement>
);
